import { useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";

declare global {
  interface Window {
    $crisp?: Array<unknown[]> & { push: (cmd: unknown[]) => void };
  }
}

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const FAQ_SECTIONS: FAQSection[] = [
  {
    title: "Abbreviation Modes",
    items: [
      {
        question: "What is Key Quotes mode?",
        answer:
          "Key Quotes extracts the most memorable, self-contained moments from an episode — the lines worth sharing verbatim. The result is a short playlist of audio clips, each one a standalone insight or story beat. It's ideal for social media snippets or quick highlights.",
      },
      {
        question: "What is Quick Catch-Up mode?",
        answer:
          "Quick Catch-Up condenses an episode to roughly 20% of its original length. Filler words, repeated points, and small talk are removed while every key idea stays in. Perfect when you want the substance of a long episode in a fraction of the time.",
      },
      {
        question: "What is Full Recap mode?",
        answer:
          "Full Recap targets about 50% compression. The full narrative arc is preserved — stories flow naturally, context is kept — but everything non-essential is trimmed. It feels like being there at half the time commitment.",
      },
      {
        question: "How does the AI decide what to keep?",
        answer:
          "We use a combination of speech-to-text transcription and large language models to identify key ideas, notable quotes, and narrative structure. The AI is trained to recognize meaningful content versus filler, tangents, and repetition. Results can vary depending on audio quality and episode structure.",
      },
    ],
  },
  {
    title: "Supported Formats & Limits",
    items: [
      {
        question: "What audio formats are supported?",
        answer:
          "We accept MP3, MP4, M4A, WAV, WebM, OGG, and FLAC files. MP3 is the most common and well-tested format.",
      },
      {
        question: "Is there a file size limit?",
        answer:
          "Yes — uploads are capped at 500 MB per file. Most podcast episodes are well under this limit even at high bitrates. If your file exceeds this, try exporting it at a lower bitrate (128 kbps is plenty for speech) or trimming any extended silence.",
      },
      {
        question: "Is there an episode length limit?",
        answer:
          "Pro subscribers can process episodes up to 2 hours long. Free plan episodes are limited to shorter durations. Very long episodes may take more time to process.",
      },
      {
        question: "Can I submit a URL instead of uploading a file?",
        answer:
          "Yes. You can paste an RSS feed URL to subscribe to a podcast, and we'll automatically process new episodes as they're released. You can also process individual episodes by URL if they are publicly accessible.",
      },
    ],
  },
  {
    title: "RSS Subscriptions",
    items: [
      {
        question: "How do RSS subscriptions work?",
        answer:
          "When you subscribe to a podcast feed, we monitor it for new episodes. Whenever a new episode is published, we automatically queue it for processing using the abbreviation mode you chose at subscription time. Results appear in your job history as soon as processing is complete.",
      },
      {
        question: "Can I backfill past episodes?",
        answer:
          "Yes. When you add a subscription you can choose how many past episodes to process — from just the latest one up to the full back catalog. Backfill jobs are queued immediately and processed in order.",
      },
      {
        question: "What happens if a feed goes down or an episode URL changes?",
        answer:
          "We retry failed episode fetches automatically. If a feed consistently fails to respond, the subscription may be paused after repeated errors. You'll see the status reflected on your Subscriptions page.",
      },
      {
        question: "Can I cancel a subscription?",
        answer:
          "Yes — open the Subscriptions page, find the feed, and remove it. Removing a subscription stops future automatic processing but does not delete results from jobs that already ran.",
      },
    ],
  },
  {
    title: "Billing & Plans",
    items: [
      {
        question: "What's included in the free plan?",
        answer:
          "The free plan includes 5 episode abbreviations so you can try the service before committing. No credit card is required to sign up.",
      },
      {
        question: "What happens when I hit my free plan limit?",
        answer:
          "Once you've used your 5 free episodes, new job submissions will be paused until you upgrade to Pro. Your existing results remain accessible. You can upgrade at any time from the Account/Billing page.",
      },
      {
        question: "How do I upgrade to Pro?",
        answer:
          "Go to the Billing page inside the app and click \"Upgrade to Pro\". You'll be taken to a Stripe checkout page to enter your payment details. Once complete, your account is upgraded instantly.",
      },
      {
        question: "Can I cancel my Pro subscription?",
        answer:
          "Yes, at any time from the Billing page. After cancellation you retain Pro access until the end of the current billing period. No partial refunds are issued for unused time.",
      },
      {
        question: "What payment methods are accepted?",
        answer:
          "We accept all major credit and debit cards through Stripe. Apple Pay and Google Pay may also be available depending on your browser and region.",
      },
      {
        question: "What if my payment fails?",
        answer:
          "Stripe will retry the payment automatically. You'll see a warning banner in the app. If payment continues to fail, your subscription may be paused. You can update your payment method at any time from the Billing page.",
      },
    ],
  },
  {
    title: "Troubleshooting",
    items: [
      {
        question: "My job has been processing for a long time — what's happening?",
        answer:
          "Processing time depends on episode length and current queue depth. A one-hour episode typically takes a few minutes. If a job has been running for more than 15 minutes, try refreshing the status page. If it still appears stuck, contact support via the chat below.",
      },
      {
        question: "The audio quality of my result sounds off — is that normal?",
        answer:
          "The output audio is re-assembled from segments of your original file, so the voice and audio quality should match your source. If something sounds wrong, it may indicate an issue with the source audio itself (e.g. variable bitrate encoding or clipping). Try re-exporting from your editing software and resubmitting.",
      },
      {
        question: "I connected my RSS feed but no new episodes are being processed.",
        answer:
          "Check the Subscriptions page to confirm the feed is active and not paused. Also verify the feed is publicly accessible without authentication. Some private or premium podcast feeds require a login and cannot be fetched automatically.",
      },
      {
        question: "I can't sign in to my account.",
        answer:
          "Try the \"Forgot password\" flow on the sign-in page. If you signed up with Google or another social provider, make sure you're using the same method. If none of those work, use the chat below and we'll help you recover access.",
      },
    ],
  },
  {
    title: "Still Need Help?",
    items: [
      {
        question: "How do I contact support?",
        answer: (
          <>
            The fastest way is to use the chat bubble in the bottom-right corner of this page — our support bot handles most common questions instantly, and a human is available for anything more complex.
            <br />
            <br />
            You can also reach us by opening the chat and leaving a message — we'll respond by email if you're not online.
          </>
        ),
      },
    ],
  },
];

function AccordionItem({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border/40 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium text-foreground hover:bg-secondary/30 transition-colors"
        aria-expanded={open}
      >
        <span>{item.question}</span>
        {open ? (
          <ChevronUp size={16} className="shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 text-sm text-muted-foreground leading-7 border-t border-border/30">
          {item.answer}
        </div>
      )}
    </div>
  );
}

function AccordionSection({ section }: { section: FAQSection }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground font-mono uppercase tracking-wider">
        {section.title}
      </h2>
      <div className="space-y-2">
        {section.items.map((item) => (
          <AccordionItem key={item.question} item={item} />
        ))}
      </div>
    </section>
  );
}

export default function HelpPage() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-mono"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-10">
          <img
            src={`${basePath}/pa-icon.jpg`}
            alt="Podcasts Abbreviated"
            className="h-8 w-8 rounded-sm object-cover"
          />
          <span className="font-mono font-bold tracking-tight uppercase text-sm text-foreground">
            Podcasts <span className="text-primary">Abbreviated</span>
          </span>
        </div>

        <div className="mb-10">
          <h1 className="text-3xl font-bold mb-2">Help & FAQ</h1>
          <p className="text-muted-foreground text-sm">
            Answers to the most common questions. Can't find what you need?{" "}
            <button
              onClick={() => {
                if (typeof window !== "undefined" && window.$crisp) {
                  window.$crisp.push(["do", "chat:open"]);
                }
              }}
              className="text-primary hover:text-primary/80 transition-colors underline underline-offset-2"
            >
              Open chat support
            </button>
            .
          </p>
        </div>

        <div className="space-y-10">
          {FAQ_SECTIONS.map((section) => (
            <AccordionSection key={section.title} section={section} />
          ))}
        </div>

        <div className="mt-16 rounded-xl border border-primary/20 bg-primary/5 p-6 flex items-start gap-4">
          <MessageCircle size={20} className="text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">
              Still stuck? We're here.
            </p>
            <p className="text-sm text-muted-foreground">
              Use the chat bubble in the bottom-right corner to reach our support team directly. Most questions get answered in minutes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
